import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations, LANG_META, DEFAULT_LANG } from "@/i18n/translations";
import { CURRENCY_META, LANG_CURRENCY, autoDetectCurrency, refreshRates } from "@/lib/currency";

const LangContext = createContext(null);

function detectInitialLang() {
  const saved = localStorage.getItem("negosino_lang");
  if (saved && translations[saved]) return saved;
  const nav = (navigator.language || "").toLowerCase();
  if (nav.startsWith("zh")) return "zh";
  if (nav.startsWith("ar")) return "ar";
  if (nav.startsWith("es")) return "es";
  if (nav.startsWith("en")) return "en";
  if (nav.startsWith("fr")) return "fr";
  return DEFAULT_LANG;
}

function detectInitialCurrency() {
  const saved = localStorage.getItem("negosino_currency");
  if (saved && CURRENCY_META[saved]) return saved;
  // Auto-detect West-African CFA zone by timezone
  const auto = autoDetectCurrency();
  if (auto && CURRENCY_META[auto]) return auto;
  return null; // fall back to lang default
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);
  const [currency, setCurrencyState] = useState(detectInitialCurrency);
  const [, setRatesTick] = useState(0);

  useEffect(() => {
    document.documentElement.lang = lang;
    const dir = LANG_META[lang]?.dir || "ltr";
    document.documentElement.dir = dir;
    document.body.classList.toggle("rtl", dir === "rtl");
  }, [lang]);

  // Refresh live exchange rates on mount (cached 12h)
  useEffect(() => {
    refreshRates().then(() => setRatesTick((t) => t + 1));
  }, []);

  const setLang = useCallback((l) => {
    if (!translations[l]) return;
    setLangState(l);
    localStorage.setItem("negosino_lang", l);
  }, []);

  const setCurrency = useCallback((c) => {
    if (c && !CURRENCY_META[c]) return;
    setCurrencyState(c);
    if (c) localStorage.setItem("negosino_currency", c);
    else localStorage.removeItem("negosino_currency");
  }, []);

  const t = useCallback(
    (key) => {
      const dict = translations[lang] || translations[DEFAULT_LANG];
      return dict[key] ?? translations[DEFAULT_LANG][key] ?? key;
    },
    [lang]
  );

  const effectiveCurrency = currency || LANG_CURRENCY[lang] || "EUR";

  return (
    <LangContext.Provider value={{ lang, setLang, t, meta: LANG_META, currency: effectiveCurrency, userCurrency: currency, setCurrency, currencyMeta: CURRENCY_META }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
export const useT = () => useContext(LangContext).t;
